import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { getRecaptchaToken } from "@/hooks/useRecaptcha";
import { projectDetailsSchema } from "@/lib/projectDetails";
import { Play } from "lucide-react";
import { REVIEW_COUNT } from "@/config/reviews";

import { PhoneFrame } from "@/components/PhoneFrame";

import logoLight from "@/assets/masonry-pro-logo-3.webp";
import heroBg from "@/assets/backyard-promo-hero.webp";
import bundleOverviewImg from "@/assets/backyard-promo-bundle-overview.webp";
import card1Img from "@/assets/backyard-promo-cambridge-pavers.webp";
import card2Img from "@/assets/backyard-promo-lit-wall.webp";
import card3Img from "@/assets/backyard-promo-fire-pit.webp";

const PHONE = "9737252395";
const PHONE_DISPLAY = "(973) 725-2395";
const CANONICAL = "https://masonrypro.gomonsteramarketing.com/lp/backyard-bundle";

const styles = `
.bp-page{
  --brand-base:#15120F;
  --brand-surface:#211C17;
  --brand-accent: hsl(42 60% 62%);
  --brand-accent-ink:#15120F;
  --brand-accent-2:#A8472B;
  --brand-text:#F2ECE3;
  --muted:#9A9088;
  --line:rgba(242,236,227,.12);
  --line-strong:rgba(242,236,227,.22);
  --maxw:1080px;
  --radius:14px;
  background:var(--brand-base);
  color:var(--brand-text);
  font-family:"Inter",system-ui,sans-serif;
  font-size:17px;
  line-height:1.55;
  -webkit-font-smoothing:antialiased;
  min-height:100vh;
}
.bp-page *{box-sizing:border-box}
.bp-page h1,.bp-page h2,.bp-page h3,.bp-page .bp-display{font-family:"Archivo",system-ui,sans-serif;letter-spacing:-.02em;line-height:1.02;margin:0}
.bp-page p{margin:0}
.bp-page .bp-wrap{max-width:var(--maxw);margin:0 auto;padding:0 22px}
.bp-page a{color:inherit}
.bp-page img{max-width:100%;display:block}

.bp-page .bp-topbar{background:var(--brand-accent);color:var(--brand-accent-ink);font-family:"Archivo",sans-serif;font-weight:700;font-size:13px;letter-spacing:.02em;text-align:center;padding:9px 16px}
.bp-page .bp-topbar span{white-space:nowrap}
.bp-page .bp-topbar .bp-dot{opacity:.5;margin:0 8px}

.bp-page .bp-head{position:sticky;top:0;z-index:40;background:rgba(21,18,15,.86);backdrop-filter:blur(10px);border-bottom:1px solid var(--line)}
.bp-page .bp-head .bp-wrap{display:flex;align-items:center;justify-content:space-between;gap:14px;padding-top:13px;padding-bottom:13px}
.bp-page .bp-logo{height:38px;width:auto;display:block}
.bp-page .bp-head-cta{display:flex;align-items:center;gap:14px;font-size:14px}
.bp-page .bp-head-phone{font-weight:700;font-family:"Archivo",sans-serif;letter-spacing:.01em;text-decoration:none}
.bp-page .bp-head-phone:hover{color:var(--brand-accent)}

.bp-page .bp-btn{display:inline-flex;align-items:center;justify-content:center;gap:9px;background:var(--brand-accent);color:var(--brand-accent-ink);font-family:"Archivo",sans-serif;font-weight:800;font-size:16px;letter-spacing:.01em;padding:15px 28px;border:none;border-radius:999px;cursor:pointer;text-decoration:none;transition:transform .15s ease, filter .15s ease}
.bp-page .bp-btn:hover{transform:translateY(-2px);filter:brightness(1.06)}
.bp-page .bp-btn.bp-ghost{background:transparent;color:var(--brand-text);border:1.5px solid var(--line-strong)}
.bp-page .bp-btn.bp-ghost:hover{border-color:var(--brand-accent);color:var(--brand-accent)}
.bp-page .bp-btn.bp-sm{padding:11px 18px;font-size:14px}
.bp-page .bp-btn.bp-block{width:100%}

.bp-page .bp-hero{position:relative;overflow:hidden;border-bottom:1px solid var(--line)}
.bp-page .bp-hero-bg{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;z-index:0}
.bp-page .bp-hero-scrim{position:absolute;inset:0;background:linear-gradient(180deg, rgba(21,18,15,.55) 0%, rgba(21,18,15,.78) 55%, rgba(21,18,15,.92) 100%);z-index:1}
.bp-page .bp-hero .bp-wrap{position:relative;z-index:2;padding-top:74px;padding-bottom:48px}
.bp-page .bp-hero-grid{display:grid;grid-template-columns:1fr;gap:36px;align-items:start}
@media(min-width:960px){.bp-page .bp-hero-grid{grid-template-columns:1.1fr .9fr;gap:48px}}
.bp-page .bp-hero-form{background:var(--brand-surface);border:1px solid var(--line-strong);border-radius:18px;padding:26px 22px}
.bp-page .bp-hero-form h3{font-size:20px;font-weight:800;margin-bottom:6px}
.bp-page .bp-hero-form .bp-fsub{color:var(--muted);font-size:14px;margin-bottom:18px}
.bp-page .bp-eyebrow{display:inline-block;font-family:"Archivo",sans-serif;font-weight:700;font-size:13px;letter-spacing:.18em;text-transform:uppercase;color:var(--brand-accent);margin-bottom:18px}
.bp-page .bp-hero h1{font-size:clamp(40px,9vw,76px);font-weight:900;margin-bottom:18px}
.bp-page .bp-lead{font-size:clamp(17px,2.4vw,21px);color:#d8d0c5;max-width:620px;margin-bottom:26px}
.bp-page .bp-hero-price{font-family:"Archivo",sans-serif;font-weight:800;font-size:18px;margin:0 0 10px;color:var(--brand-text)}
.bp-page .bp-hero-price .bp-hero-strike{color:var(--muted);text-decoration:line-through;text-decoration-color:var(--brand-accent-2);text-decoration-thickness:2px;margin-right:10px}
.bp-page .bp-hero-price .bp-hero-big{color:var(--brand-accent);font-size:28px}
.bp-page .bp-hero-save{font-size:15px;color:var(--brand-text);margin:0 0 10px}
.bp-page .bp-hero-save em{color:var(--brand-accent-2);font-style:normal}
.bp-page .bp-hero-disc{font-size:12px;color:var(--muted);margin:0 0 26px}
.bp-page .bp-checks{display:flex;flex-wrap:wrap;gap:10px 20px;margin-top:30px}
.bp-page .bp-check{display:flex;align-items:center;gap:8px;font-weight:600;font-size:15px}
.bp-page .bp-check svg{flex:none;color:var(--brand-accent)}

.bp-page .bp-section{padding:66px 0;border-bottom:1px solid var(--line)}
.bp-page .bp-s-eyebrow{font-family:"Archivo",sans-serif;font-weight:700;font-size:12px;letter-spacing:.2em;text-transform:uppercase;color:var(--brand-accent);margin-bottom:14px}
.bp-page .bp-s-title{font-size:clamp(28px,5vw,46px);font-weight:800;margin-bottom:14px}
.bp-page .bp-s-sub{color:var(--muted);max-width:600px;margin-bottom:38px;font-size:17px}

.bp-page .bp-overview{margin:0 auto 36px;max-width:500px;text-align:center}
.bp-page .bp-overview img{width:100%;height:auto;border-radius:var(--radius);border:1px solid var(--line);display:block}
.bp-page .bp-overview .bp-cap{margin-top:10px;font-size:13px;color:var(--muted);font-style:italic}
.bp-page .bp-included-layout{display:flex;gap:30px;align-items:flex-start}
.bp-page .bp-included-left{width:43%;flex-shrink:0}
.bp-page .bp-included-left .bp-overview{max-width:none;margin:0;text-align:center}
.bp-page .bp-included-right{flex:1;display:flex;flex-direction:column;gap:14px}
.bp-page .bp-card-h{background:var(--brand-surface);border:1px solid var(--line);border-radius:var(--radius);overflow:hidden;display:flex;flex-direction:row;position:relative}
.bp-page .bp-card-h-img{width:100px;height:100px;flex-shrink:0;object-fit:cover;display:block}
.bp-page .bp-card-h-body{padding:14px 16px;display:flex;flex-direction:column;justify-content:center;flex:1;min-width:0}
.bp-page .bp-card-h-body .bp-num{font-family:"Archivo",sans-serif;font-weight:800;font-size:12px;letter-spacing:.1em;color:var(--brand-accent);margin-bottom:6px}
.bp-page .bp-card-h-body h3{font-size:17px;font-weight:800;margin-bottom:2px}
.bp-page .bp-card-h-body .bp-qty{font-family:"Archivo",sans-serif;font-weight:700;font-size:11px;letter-spacing:.08em;text-transform:uppercase;color:var(--muted);margin-bottom:8px}
.bp-page .bp-card-h-body p{color:#cfc7bc;font-size:13px;line-height:1.4;margin-bottom:10px;flex:1}
.bp-page .bp-card-h-body .bp-retail{display:flex;justify-content:space-between;align-items:baseline;border-top:1px solid var(--line);padding-top:8px;font-size:11px;letter-spacing:.06em;text-transform:uppercase;color:var(--muted);font-family:"Archivo",sans-serif;font-weight:700}
.bp-page .bp-card-h-body .bp-retail b{color:var(--brand-text);font-size:15px;letter-spacing:0;text-transform:none}
.bp-page .bp-cards{display:grid;grid-template-columns:repeat(3,1fr);gap:14px}
.bp-page .bp-card{background:var(--brand-surface);border:1px solid var(--line);border-radius:var(--radius);overflow:hidden;position:relative;display:flex;flex-direction:column}
.bp-page .bp-card-img{height:130px;width:100%;object-fit:cover;display:block}
.bp-page .bp-card-body{padding:16px 16px 18px;display:flex;flex-direction:column;flex:1}
.bp-page .bp-card .bp-num{font-family:"Archivo",sans-serif;font-weight:800;font-size:12px;letter-spacing:.1em;color:var(--brand-accent);margin-bottom:8px}
.bp-page .bp-card h3{font-size:18px;font-weight:800;margin-bottom:2px}
.bp-page .bp-card .bp-qty{font-family:"Archivo",sans-serif;font-weight:700;font-size:11px;letter-spacing:.08em;text-transform:uppercase;color:var(--muted);margin-bottom:10px}
.bp-page .bp-card p{color:#cfc7bc;font-size:13.5px;line-height:1.45;margin-bottom:14px;flex:1}
.bp-page .bp-card .bp-retail{display:flex;justify-content:space-between;align-items:baseline;border-top:1px solid var(--line);padding-top:10px;font-size:11px;letter-spacing:.06em;text-transform:uppercase;color:var(--muted);font-family:"Archivo",sans-serif;font-weight:700}
.bp-page .bp-card .bp-retail b{color:var(--brand-text);font-size:15px;letter-spacing:0;text-transform:none}

.bp-page .bp-priceblock{margin-top:34px;background:var(--brand-surface);border:1px solid var(--line-strong);border-radius:18px;padding:38px 28px;text-align:center}
.bp-page .bp-priceblock .bp-label{font-family:"Archivo",sans-serif;font-weight:700;letter-spacing:.16em;text-transform:uppercase;font-size:13px;color:var(--muted);margin-bottom:10px}
.bp-page .bp-priceblock .bp-strike{font-size:24px;color:var(--muted);text-decoration:line-through;text-decoration-color:var(--brand-accent-2);text-decoration-thickness:2px}
.bp-page .bp-priceblock .bp-big{font-family:"Archivo",sans-serif;font-weight:900;font-size:clamp(54px,13vw,92px);line-height:.95;margin:6px 0 4px;color:var(--brand-accent)}
.bp-page .bp-priceblock .bp-save{font-weight:700;font-size:16px;color:var(--brand-text);margin-bottom:24px}
.bp-page .bp-priceblock .bp-save em{color:var(--brand-accent-2);font-style:normal}
.bp-page .bp-fineprint{font-size:12px;color:var(--muted);margin-top:18px;line-height:1.5}

.bp-page .bp-shorts-grid{display:grid;grid-template-columns:1fr;gap:36px;justify-items:center;margin-top:8px}
@media(min-width:760px){.bp-page .bp-shorts-grid{grid-template-columns:repeat(3,minmax(0,1fr));gap:28px}}
.bp-page .bp-short{display:flex;flex-direction:column;align-items:center;width:100%;max-width:260px}
.bp-page .bp-short-cap{margin-top:14px;text-align:center;font-family:"Archivo",sans-serif;font-weight:700;font-size:13px;letter-spacing:.04em;color:var(--brand-text)}
.bp-page .bp-short-loc{margin-top:4px;text-align:center;font-size:12px;color:var(--muted)}
.bp-page .bp-short-shield{position:absolute;inset:0;z-index:5;pointer-events:none}

.bp-page .bp-stats{display:grid;grid-template-columns:1fr 1fr;gap:1px;background:var(--line);border:1px solid var(--line);border-radius:var(--radius);overflow:hidden}
.bp-page .bp-stat{background:var(--brand-base);padding:28px 22px}
.bp-page .bp-stat .bp-n{font-family:"Archivo",sans-serif;font-weight:900;font-size:46px;color:var(--brand-accent);line-height:1}
.bp-page .bp-stat .bp-t{font-family:"Archivo",sans-serif;font-weight:700;font-size:14px;letter-spacing:.04em;margin:8px 0 6px}
.bp-page .bp-stat .bp-d{font-size:14px;color:var(--muted)}
.bp-page .bp-quote{margin-top:34px;background:var(--brand-surface);border:1px solid var(--line);border-left:3px solid var(--brand-accent);border-radius:12px;padding:28px 26px}
.bp-page .bp-quote .bp-stars{color:var(--brand-accent);letter-spacing:4px;font-size:15px;margin-bottom:12px}
.bp-page .bp-quote p{font-size:19px;line-height:1.5;margin-bottom:14px}
.bp-page .bp-quote .bp-by{font-family:"Archivo",sans-serif;font-weight:700;font-size:14px;color:var(--muted)}

.bp-page .bp-reviews{margin-top:34px;display:grid;grid-template-columns:repeat(3,1fr);gap:20px}
@media (max-width:900px){.bp-page .bp-reviews{grid-template-columns:1fr}}
.bp-page .bp-rcard{background:var(--brand-surface);border:1px solid var(--line);border-radius:12px;padding:24px 22px;display:flex;flex-direction:column}
.bp-page .bp-rcard .bp-rq{color:var(--brand-accent);opacity:.45;font-size:28px;line-height:1;font-family:Georgia,serif;margin-bottom:10px}
.bp-page .bp-rcard .bp-stars{color:var(--brand-accent);letter-spacing:3px;font-size:14px;margin-bottom:12px}
.bp-page .bp-rcard p{font-size:15px;line-height:1.55;margin-bottom:18px;color:var(--brand-text)}
.bp-page .bp-rcard .bp-rby{margin-top:auto;padding-top:14px;border-top:1px solid var(--line)}
.bp-page .bp-rcard .bp-rname{font-family:"Archivo",sans-serif;font-weight:700;font-size:15px;color:var(--brand-text)}
.bp-page .bp-rcard .bp-rsrc{font-size:12px;color:var(--muted);margin-top:2px}

.bp-page .bp-reviews-heading{text-align:center;margin-bottom:28px}
.bp-page .bp-reviews-heading .bp-rh-stars{color:var(--brand-accent);letter-spacing:3px;font-size:18px;margin-bottom:8px}
.bp-page .bp-reviews-heading .bp-rh-stars .bp-rh-num{font-family:"Archivo",sans-serif;font-weight:800;font-size:24px;color:var(--brand-text);margin-left:8px;vertical-align:middle}
.bp-page .bp-reviews-heading .bp-rh-title{font-family:"Archivo",sans-serif;font-weight:800;font-size:clamp(28px,5vw,46px);margin-bottom:8px}
.bp-page .bp-reviews-heading .bp-rh-sub{color:var(--muted);font-size:17px}

.bp-page .bp-faq{max-width:760px}
.bp-page .bp-faq details{border-bottom:1px solid var(--line);padding:4px 0}
.bp-page .bp-faq summary{cursor:pointer;list-style:none;display:flex;justify-content:space-between;align-items:center;gap:16px;padding:20px 2px;font-family:"Archivo",sans-serif;font-weight:700;font-size:18px}
.bp-page .bp-faq summary::-webkit-details-marker{display:none}
.bp-page .bp-faq summary .bp-pm{flex:none;color:var(--brand-accent);font-size:24px;font-weight:700;transition:transform .2s ease}
.bp-page .bp-faq details[open] summary .bp-pm{transform:rotate(45deg)}
.bp-page .bp-faq .bp-ans{padding:0 2px 22px;color:#cfc7bc;font-size:16px;max-width:680px}

.bp-page .bp-final{background:var(--brand-surface);border-bottom:none}
.bp-page .bp-final .bp-s-title{max-width:640px}
.bp-page .bp-cta-row{display:flex;flex-wrap:wrap;gap:14px;margin:8px 0 40px}
.bp-page .bp-formcard{background:var(--brand-base);border:1px solid var(--line-strong);border-radius:18px;padding:30px 26px;max-width:560px}
.bp-page .bp-formcard h3{font-size:22px;font-weight:800;margin-bottom:6px}
.bp-page .bp-formcard .bp-fsub{color:var(--muted);font-size:15px;margin-bottom:22px}
.bp-page .bp-field{margin-bottom:14px}
.bp-page .bp-field label{display:block;font-size:13px;font-weight:600;color:var(--muted);margin-bottom:6px}
.bp-page .bp-field input,.bp-page .bp-field textarea{width:100%;background:#fff;border:1px solid var(--line-strong);border-radius:10px;color:#15120F;font:inherit;font-size:16px;padding:13px 14px}
.bp-page .bp-field input::placeholder,.bp-page .bp-field textarea::placeholder{color:#8a857d}
.bp-page .bp-field input:focus,.bp-page .bp-field textarea:focus{outline:2px solid var(--brand-accent);outline-offset:1px;border-color:transparent}
.bp-page .bp-row2{display:grid;grid-template-columns:1fr 1fr;gap:14px}
.bp-page .bp-consent{display:flex;gap:10px;align-items:flex-start;margin:6px 0 8px;font-size:12.5px;color:var(--muted);line-height:1.5}
.bp-page .bp-consent input{margin-top:3px;flex:none;width:16px;height:16px;accent-color:var(--brand-accent)}
.bp-page .bp-privlink{font-size:12px;color:var(--muted);margin:0 0 18px 26px}
.bp-page .bp-privlink a{color:var(--brand-accent);text-decoration:underline}
.bp-page .bp-legend{font-size:12px;color:var(--muted);margin:0 0 12px 26px;text-align:left}
.bp-page .bp-thanks{text-align:center;padding:30px 10px}
.bp-page .bp-thanks h3{color:var(--brand-accent);font-size:26px;margin-bottom:8px}
.bp-page .bp-err{color:#ff8a7a;font-size:13px;margin:6px 0 0 26px}
.bp-page .bp-honey{position:absolute;left:-9999px;top:-9999px;height:0;width:0;opacity:0}

.bp-page .bp-banner{background:var(--brand-accent-2);color:#fff;text-align:center;font-family:"Archivo",sans-serif;font-weight:800;letter-spacing:.06em;font-size:14px;padding:13px 16px}
.bp-page .bp-footer{padding:40px 0;font-size:14px;color:var(--muted)}
.bp-page .bp-footer .bp-wrap{display:flex;flex-wrap:wrap;gap:18px 40px;justify-content:space-between}
.bp-page .bp-footer b{color:var(--brand-text)}
.bp-page .bp-footer a{color:var(--brand-accent);text-decoration:none}
.bp-page .bp-lic{font-family:"Archivo",sans-serif;font-weight:700;color:var(--brand-text)}

@media(max-width:680px){
  .bp-page .bp-head .bp-wrap{padding-top:11px;padding-bottom:11px}
  .bp-page .bp-head-cta .bp-btn{display:none}
  .bp-page .bp-topbar{font-size:11px}
  .bp-page .bp-topbar .bp-dot{margin:0 5px}
  .bp-page .bp-stats{grid-template-columns:1fr 1fr}
  .bp-page .bp-row2{grid-template-columns:1fr}
  .bp-page .bp-cards{grid-template-columns:1fr}
  .bp-page .bp-overview{max-width:360px}
  .bp-page .bp-included-layout{flex-direction:column}
  .bp-page .bp-included-left{width:100%}
  .bp-page .bp-card-h{flex-direction:column}
  .bp-page .bp-card-h-img{width:100%;height:130px}
}
@media(prefers-reduced-motion:reduce){
  .bp-page *{scroll-behavior:auto;transition:none!important}
}
`;

const Check = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden="true">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

type BpShort = { id: string; title: string; location: string };

const BP_SHORTS: BpShort[] = [
  { id: "_8_1uw8ZHAk", title: "Backyard Transformation", location: "North Jersey" },
  { id: "mRP5CzotoD0", title: "Paver Patio Build", location: "North Jersey" },
  { id: "L6zFeqCTTSI", title: "Hardscape Project", location: "North Jersey" },
];

const BP_THUMB_FORMATS = ["maxresdefault.jpg", "hq720.jpg", "sddefault.jpg", "hqdefault.jpg"];

function BpPhoneShort({ video }: { video: BpShort }) {
  const [loaded, setLoaded] = useState(false);
  const [thumbIdx, setThumbIdx] = useState(0);
  const thumbnailUrl = `https://i.ytimg.com/vi/${video.id}/${BP_THUMB_FORMATS[thumbIdx]}`;
  const embedSrc = `https://www.youtube-nocookie.com/embed/${video.id}?autoplay=1&rel=0&modestbranding=1&playsinline=1&controls=1&iv_load_policy=3&fs=0&disablekb=1`;

  return (
    <div className="bp-short">
      <PhoneFrame>
        {loaded ? (
          <>
            <iframe
              src={embedSrc}
              title={video.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              className="absolute inset-0 h-full w-full"
              onContextMenu={(e) => e.preventDefault()}
            />
          </>
        ) : (
          <button
            type="button"
            onClick={() => setLoaded(true)}
            className="group absolute inset-0 h-full w-full"
            aria-label={`Play ${video.title}`}
          >
            <img
              src={thumbnailUrl}
              alt={video.title}
              className="h-full w-full object-cover"
              loading="lazy"
              decoding="async"
              onError={() => {
                if (thumbIdx < BP_THUMB_FORMATS.length - 1) setThumbIdx((i) => i + 1);
              }}
            />
            <div className="absolute inset-0 bg-black/30 transition-colors duration-300 group-hover:bg-black/10" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full" style={{ background: "hsl(42 60% 62%)" }}>
                <Play className="ml-1 h-6 w-6" style={{ fill: "#15120F", color: "#15120F" }} />
              </div>
            </div>
          </button>
        )}
      </PhoneFrame>
      <div className="bp-short-cap">{video.title}</div>
      <div className="bp-short-loc">{video.location}</div>
    </div>
  );
}

function LeadForm() {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);
  const [smsError, setSmsError] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", city: "", state: "", zipCode: "", notes: "" });

  const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (honeypot) return;
    const details = projectDetailsSchema.max(493).safeParse(form.notes);
    if (!details.success) {
      alert(details.error.issues[0]?.message || "Please tell us about your project.");
      return;
    }
    if (!smsConsent) { setSmsError(true); return; }
    setSubmitting(true);
    try {
      const recaptchaToken = await getRecaptchaToken("submit_lead");
      const { supabase } = await import("@/integrations/supabase/client");
      const noteBody = `Notes: ${details.data}`;
      const { error } = await supabase.functions.invoke("submit-lead", {
        body: {
          name: form.name,
          phone: form.phone,
          email: form.email,
          address: form.address,
          city: form.city,
          state: form.state,
          zipCode: form.zipCode,
          message: noteBody,
          source: "lp-backyard-bundle",
          privacyConsent: true,
          smsConsent,
          recaptchaToken,
        },
      });
      if (error) throw error;
      if (typeof window !== "undefined") {
        if (typeof (window as any).fbq === "function") {
          (window as any).fbq("track", "Lead");
        }
        (window as any).dataLayer = (window as any).dataLayer || [];
        (window as any).dataLayer.push({
          event: "form_submit",
          form_name: "backyard_bundle",
          form_source: "lp-backyard-bundle",
          page_path: "/lp/backyard-bundle",
        });
        (window as any).dataLayer.push({
          event: "lead_form_submit",
          form_name: "backyard_bundle",
        });
      }
      setSubmitted(true);
      // Give the tracking pixels ~400ms to send before navigating away.
      setTimeout(() => {
        window.location.assign("/lp/backyard-bundle/thank-you");
      }, 400);
    } catch (err) {
      console.error("backyard-bundle submit error:", err);
      alert("Something went wrong. Please call or text us at (973) 725-2395.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bp-thanks">
        <h3>You're on the list.</h3>
        <p>We will reach out shortly to confirm your slot and your number. Need us sooner? Call or text {PHONE_DISPLAY}.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <h3>Lock In My Price</h3>
      <p className="bp-fsub">Tell us where you are and we will confirm your number on a free on-site visit.</p>
      <div className="bp-honey" aria-hidden="true">
        <label htmlFor="bp-website">Website</label>
        <input id="bp-website" name="website" type="text" tabIndex={-1} autoComplete="off" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} />
      </div>
      <div className="bp-field"><label htmlFor="bp-name">Name*</label><input id="bp-name" name="name" type="text" required value={form.name} onChange={onChange} /></div>
      <div className="bp-row2">
        <div className="bp-field"><label htmlFor="bp-phone">Phone*</label><input id="bp-phone" name="phone" type="tel" required value={form.phone} onChange={onChange} /></div>
        <div className="bp-field"><label htmlFor="bp-email">Email*</label><input id="bp-email" name="email" type="email" required value={form.email} onChange={onChange} /></div>
      </div>
      <div className="bp-field"><label htmlFor="bp-address">Street Address*</label><input id="bp-address" name="address" type="text" required value={form.address} onChange={onChange} /></div>
      <div className="bp-row2">
        <div className="bp-field"><label htmlFor="bp-city">City*</label><input id="bp-city" name="city" type="text" required placeholder="e.g. Wayne" value={form.city} onChange={onChange} /></div>
        <div className="bp-field"><label htmlFor="bp-state">State*</label><input id="bp-state" name="state" type="text" required placeholder="NJ" value={form.state} onChange={onChange} /></div>
      </div>
      <div className="bp-field"><label htmlFor="bp-zip">Zip Code*</label><input id="bp-zip" name="zipCode" type="text" required value={form.zipCode} onChange={onChange} /></div>
      <div className="bp-field"><label htmlFor="bp-notes">Tell us about your project*</label><textarea id="bp-notes" name="notes" rows={2} required maxLength={493} value={form.notes} onChange={onChange} /></div>
      <label className="bp-consent">
        <input type="checkbox" checked={smsConsent} onChange={(e) => { setSmsConsent(e.target.checked); if (e.target.checked) setSmsError(false); }} />
        <span>I agree to be contacted by Masonry Pro Construction about my estimate, including by text. Message and data rates may apply. Reply STOP to opt out.</span>
      </label>
      <p className="bp-privlink">See our <Link to="/privacy-policy">Privacy Policy</Link>.</p>
      {smsError && <p className="bp-err">Please check the box above so we can contact you about your estimate.</p>}
      <p className="bp-legend">* Required. Good details now mean an honest, accurate estimate later.</p>
      <button className="bp-btn bp-block" type="submit" disabled={submitting}>{submitting ? "Sending…" : "Lock In My Price"}</button>
    </form>
  );
}

export default function BackyardPromo() {
  return (
    <div className="bp-page">
      <Helmet>
        <title>$19,900 Backyard Bundle | Masonry Pro Construction | North Jersey</title>
        <meta name="description" content="One fixed price for a complete North Jersey backyard build. Premium paver patio, lit stone wall, and a custom fire pit. Free on-site quote. NJ HIC #13VH06777200." />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href={CANONICAL} />
        <meta property="og:title" content="$19,900 Backyard Bundle | Masonry Pro Construction" />
        <meta property="og:url" content={CANONICAL} />
        <meta property="og:type" content="website" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link href="https://fonts.googleapis.com/css2?family=Archivo:wght@500;600;700;800;900&family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet" />
        <style>{styles}</style>
      </Helmet>

      {/* Top trust bar */}
      <div className="bp-topbar">
        <span>NJ HIC License #13VH06777200</span><span className="bp-dot">/</span>
        <span>Licensed &amp; Insured</span><span className="bp-dot">/</span>
        <span>10+ Year Warranty</span><span className="bp-dot">/</span>
        <span>Financing Available</span><span className="bp-dot">/</span>
        <span>Free On-Site Quote</span>
      </div>

      {/* Header */}
      <header className="bp-head">
        <div className="bp-wrap">
          <a href="#top" aria-label="Masonry Pro Construction"><img src={logoLight} alt="Masonry Pro Construction" className="bp-logo" /></a>
          <div className="bp-head-cta">
            <a className="bp-head-phone" href={`tel:${PHONE}`}>{PHONE_DISPLAY}</a>
            <a className="bp-btn bp-sm" href="#quote">Lock In Price</a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="bp-hero" id="top">
        <img src={heroBg} alt="Finished paver patio with fire pit in Bloomfield, New Jersey" className="bp-hero-bg" fetchPriority="high" decoding="async" />
        <div className="bp-hero-scrim" />
        <div className="bp-wrap">
          <div className="bp-hero-grid">
            <div>
              <span className="bp-eyebrow">Backyard Bundle</span>
              <h1>Three Projects.<br />One Crew.<br />One Price.</h1>
              <p className="bp-lead">A complete backyard build for one fixed number. Paver patio, lit stone wall, and a custom fire pit, installed by our own crew across North Jersey. We give you a clear number up front and we stand behind it.</p>
              <p className="bp-hero-price"><span className="bp-hero-strike">$32,000</span><span className="bp-hero-big">$19,900*</span></p>
              <p className="bp-hero-save">Save <em>$12,100</em> on the standard backyard package. Financing available.</p>
              <p className="bp-hero-disc">*Based on a standard install. Final price confirmed at your free on-site visit.</p>
              <div className="bp-checks">
                <div className="bp-check"><Check /> Licensed &amp; Insured</div>
                <div className="bp-check"><Check /> 10+ Year Warranty</div>
                <div className="bp-check"><Check /> Financing Available</div>
                <div className="bp-check"><Check /> Free On-Site Quote</div>
              </div>
            </div>
            <div className="bp-hero-form" id="quote">
              <LeadForm />
            </div>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="bp-section">
        <div className="bp-wrap">
          <div className="bp-s-eyebrow">What's Included</div>
          <h2 className="bp-s-title">Every component, every dollar accounted for.</h2>
          <p className="bp-s-sub">Most masonry quotes are a black box of "it depends." This one is not. Here is exactly what you get for $19,900.</p>

          <div className="bp-included-layout">
            <div className="bp-included-left">
              <figure className="bp-overview">
                <img src={bundleOverviewImg} alt="Annotated overview of the backyard bundle showing the paver patio, lit stone wall, and fire pit" loading="lazy" decoding="async" />
                <figcaption className="bp-cap">Everything in the bundle, in one yard.</figcaption>
              </figure>
            </div>
            <div className="bp-included-right">
              <div className="bp-card-h">
                <img src={card1Img} alt="Close-up of Cambridge ArmorTec pavers" className="bp-card-h-img" loading="lazy" decoding="async" />
                <div className="bp-card-h-body">
                  <div className="bp-num">/ 01</div>
                  <h3>Premium Pavers</h3>
                  <div className="bp-qty">500 SqFt</div>
                  <p>Cambridge ArmorTec pavers over a compacted base with polymeric joint sand. Lifetime color guarantee.</p>
                  <div className="bp-retail">Retail Value <b>~$15,000</b></div>
                </div>
              </div>
              <div className="bp-card-h">
                <img src={card2Img} alt="Illuminated stone retaining wall with low-voltage LED lighting" className="bp-card-h-img" loading="lazy" decoding="async" />
                <div className="bp-card-h-body">
                  <div className="bp-num">/ 02</div>
                  <h3>Lit Stone Wall</h3>
                  <div className="bp-qty">50 Linear Ft</div>
                  <p>Engineered retaining and seating wall with built-in low-voltage LED lighting.</p>
                  <div className="bp-retail">Retail Value <b>~$10,000</b></div>
                </div>
              </div>
              <div className="bp-card-h">
                <img src={card3Img} alt="Custom hand-built modern fire pit with stone surround" className="bp-card-h-img" loading="lazy" decoding="async" />
                <div className="bp-card-h-body">
                  <div className="bp-num">/ 03</div>
                  <h3>Custom Fire Pit</h3>
                  <div className="bp-qty">The Centerpiece</div>
                  <p>Hand-built modern fire pit with a stone surround, wood or gas ready.</p>
                  <div className="bp-retail">Retail Value <b>~$7,000</b></div>
                </div>
              </div>
            </div>
          </div>

          <div className="bp-priceblock">
            <div className="bp-label">Bundle Price</div>
            <div className="bp-strike">$32,000</div>
            <div className="bp-big">$19,900*</div>
            <div className="bp-save">You save <em>$12,100</em>. Financing available.</div>
            <a className="bp-btn" href="#quote">Lock In This Price</a>
            <div className="bp-fineprint">*$19,900 package price is based on a standard 500 sqft installation on a level, accessible yard. Your exact price is confirmed at your free on-site visit and can vary with yard size, site access, grading and drainage, soil and base conditions, removal of existing structures, material selection, and permit or gas-hookup needs. Once we provide a written quote, that is your number. The $32,000 figure reflects the typical cost of these components when quoted separately as custom work; actual separate-build costs vary.</div>
          </div>
        </div>
      </section>

      {/* Before & After */}
      <section className="bp-section">
        <div className="bp-wrap">
          <div className="bp-s-eyebrow">Real Yards. Real Crews.</div>
          <h2 className="bp-s-title">See The Work In Action.</h2>
          <p className="bp-s-sub">Real installs from North Jersey homeowners. No stock footage, no render tricks.</p>
          <div className="bp-shorts-grid">
            {BP_SHORTS.map((v) => (
              <BpPhoneShort key={v.id} video={v} />
            ))}
          </div>
        </div>
      </section>

      {/* Why Masonry Pro */}
      <section className="bp-section">
        <div className="bp-wrap">
          <div className="bp-s-eyebrow">Why Masonry Pro</div>
          <h2 className="bp-s-title">Built To Outlast The Mortgage.</h2>
          <div className="bp-stats">
            <div className="bp-stat"><div className="bp-n">10+</div><div className="bp-t">Year Warranty</div><div className="bp-d">On workmanship and materials. Written and honored.</div></div>
            <div className="bp-stat"><div className="bp-n">500+</div><div className="bp-t">Yards Built</div><div className="bp-d">Across Bergen, Passaic, Essex, and Morris counties.</div></div>
            <div className="bp-stat"><div className="bp-n">1</div><div className="bp-t">Fixed Price</div><div className="bp-d">We quote you Monday. Same number Friday.</div></div>
            <div className="bp-stat"><div className="bp-n">0</div><div className="bp-t">Subcontractors</div><div className="bp-d">Our crew, our trucks, our name on the job.</div></div>
          </div>
          {/* Paver-patio Google reviews. */}
          <div className="bp-reviews-heading">
            <div className="bp-rh-stars">★ ★ ★ ★ ★ <span className="bp-rh-num">5.0</span></div>
            <h2 className="bp-rh-title">What Our Customers Say</h2>
            <p className="bp-rh-sub">{`${REVIEW_COUNT}+ Google Reviews from North Jersey homeowners`}</p>
          </div>
          <div className="bp-reviews">
            <div className="bp-rcard">
              <div className="bp-rq">“</div>
              <div className="bp-stars">★ ★ ★ ★ ★</div>
              <p>"The transformation of our backyard was beyond our expectations. The paver patio they installed has become the centerpiece of all our family gatherings. Beautiful craftsmanship!"</p>
              <div className="bp-rby">
                <div className="bp-rname">Joey P.</div>
                <div className="bp-rsrc">Google Review</div>
              </div>
            </div>
            <div className="bp-rcard">
              <div className="bp-rq">“</div>
              <div className="bp-stars">★ ★ ★ ★ ★</div>
              <p>"Masonry Pro Construction is an outstanding partner in paver/masonry projects. The design consultation, finished product and post-job clean-up are second to none. The owners and crew are very nice too. They made our property beautiful. Highly recommend!"</p>
              <div className="bp-rby">
                <div className="bp-rname">Margaret L.</div>
                <div className="bp-rsrc">Google Review</div>
              </div>
            </div>
            <div className="bp-rcard">
              <div className="bp-rq">“</div>
              <div className="bp-stars">★ ★ ★ ★ ★</div>
              <p>"The fire pit area they created has become our favorite spot. Quality stonework, perfect placement, and they even added subtle lighting. Worth every penny!"</p>
              <div className="bp-rby">
                <div className="bp-rname">Josh F.</div>
                <div className="bp-rsrc">Google Review</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bp-section">
        <div className="bp-wrap">
          <div className="bp-s-eyebrow">Common Questions</div>
          <h2 className="bp-s-title">No Surprises.</h2>
          <div className="bp-faq">
            <details open>
              <summary>What if my yard is bigger or smaller than 500 sqft?<span className="bp-pm">+</span></summary>
              <div className="bp-ans">The package is calibrated for the average North Jersey backyard. We can scale it up or down, pricing adjusts proportionally, and we give you the exact number on the free site visit. No mystery math.</div>
            </details>
            <details>
              <summary>How does the financing work?<span className="bp-pm">+</span></summary>
              <div className="bp-ans">We work with several home improvement lenders. Most homeowners qualify for $0 down with flexible terms. Applying takes about 60 seconds and it is a soft credit pull only. Financing is subject to credit approval and terms vary.</div>
            </details>
            <details>
              <summary>How long does the install take?<span className="bp-pm">+</span></summary>
              <div className="bp-ans">A typical install runs 7 to 10 working days from break ground to walk through, weather permitting. We work clean and we work fast.</div>
            </details>
            <details>
              <summary>What is not included?<span className="bp-pm">+</span></summary>
              <div className="bp-ans">Permits (rare for this scope, but if needed we handle them at cost), demolition of existing structures over 100 sqft, and gas hookup for the fire pit if you choose gas. Everything is disclosed upfront.</div>
            </details>
            <details>
              <summary>Why is this cheaper than other quotes?<span className="bp-pm">+</span></summary>
              <div className="bp-ans">We standardized one package, same materials, same dimensions, same install workflow. That is how we cut the price 35 to 40% versus a fully custom build without cutting quality. You get volume pricing on a custom-feel result.</div>
            </details>
            <details>
              <summary>What areas do you service?<span className="bp-pm">+</span></summary>
              <div className="bp-ans">North Jersey: Bergen, Passaic, Essex, and Morris counties. Just outside that line? Give us a call and we will let you know.</div>
            </details>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bp-section bp-final">
        <div className="bp-wrap">
          <div className="bp-s-eyebrow">Lock Your Slot</div>
          <h2 className="bp-s-title">Stop Looking At The Same Yard.</h2>
          <p className="bp-s-sub">We are booking summer and fall 2026 installs now. Lock your price and your slot in under 60 seconds.</p>
          <div className="bp-cta-row">
            <a className="bp-btn" href={`tel:${PHONE}`}>Call {PHONE_DISPLAY}</a>
            <a className="bp-btn bp-ghost" href={`sms:${PHONE}`}>Text Us</a>
          </div>
          <a className="bp-btn" href="#quote">Back To Quote Form</a>
        </div>
      </section>

      {/* Urgency banner */}
      <div className="bp-banner">Slots filling fast, lock yours today.</div>

      {/* Footer */}
      <footer className="bp-footer">
        <div className="bp-wrap">
          <div>
            <b>Masonry Pro Construction</b><br />
            36 Highview Ave, Totowa, NJ 07512<br />
            <a href={`tel:${PHONE}`}>{PHONE_DISPLAY}</a>
          </div>
          <div>
            Serving Bergen, Passaic, Essex &amp; Morris counties, North Jersey.<br />
            Family owned and operated, 35+ years.
          </div>
          <div className="bp-lic">NJ HIC License #13VH06777200</div>
        </div>
      </footer>
    </div>
  );
}